import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { UpdateCompetition } from "~pages/Competitions/types/upsertCompetition.ts";
import type { Competition, CompetitionResponse } from "~types/competition.ts";

export const useUpdateCompetition = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<Competition, Error, UpdateCompetition>({
    mutationFn: async ({ competitionId, ...body }) => {
      const response = await request<CompetitionResponse>(`${sport}/o/${organizationId}/competitions/${competitionId}`, {
        method: "PUT",
        body,
      });

      return response?.data?.[0];
    },
    onSuccess: async (updatedCompetition) => {
      if (!updatedCompetition?.competitionId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingCompetition = updatedScope.resources?.competitions?.[updatedCompetition.competitionId];

        if (existingCompetition && updatedScope.resources.competitions) {
          updatedScope.resources.competitions[updatedCompetition.competitionId] = {
            ...existingCompetition,
            ...updatedCompetition,
            competitionId: updatedCompetition.competitionId,
          };
        }
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["competitions", sport, organizationId, updatedCompetition.competitionId] }),
      ]);
    },
  });
};
